/**
 * DTW Dynamic Time Warping implementation for AR-4 edging sync.
 * Standard DTW with cumulative matrix, cost = abs(a - b).
 */

export interface DTWResult {
  distance: number;          // normalized by (n + m)
  alignmentScore: number;    // 0-100, higher is better
  path: [number, number][];  // optimal warping path
}

/**
 * Compute DTW distance between two sequences.
 * @param seriesA First sequence
 * @param seriesB Second sequence
 * @returns DTWResult with normalized distance and alignment score
 */
export function dtw(seriesA: number[], seriesB: number[]): DTWResult {
  const n = seriesA.length;
  const m = seriesB.length;
  
  if (n === 0 || m === 0) {
    return { distance: 0, alignmentScore: 0, path: [] };
  }
  
  // Build cost matrix
  const cost: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: m }, (_, j) => Math.abs(seriesA[i] - seriesB[j]))
  );
  
  // Build cumulative matrix
  const dtwMatrix: number[][] = Array.from({ length: n }, () => new Array(m).fill(Infinity));
  dtwMatrix[0][0] = cost[0][0];
  
  // Fill first row and column
  for (let j = 1; j < m; j++) dtwMatrix[0][j] = dtwMatrix[0][j - 1] + cost[0][j];
  for (let i = 1; i < n; i++) dtwMatrix[i][0] = dtwMatrix[i - 1][0] + cost[i][0];
  
  // Fill rest
  for (let i = 1; i < n; i++) {
    for (let j = 1; j < m; j++) {
      dtwMatrix[i][j] = cost[i][j] + Math.min(
        dtwMatrix[i - 1][j],      // insertion
        dtwMatrix[i][j - 1],      // deletion
        dtwMatrix[i - 1][j - 1]   // match
      );
    }
  }
  
  // Normalize by path length
  const dtwDistance = dtwMatrix[n - 1][m - 1] / (n + m);
  
  // Backtrack to find path
  const path: [number, number][] = [];
  let i = n - 1, j = m - 1;
  path.push([i, j]);
  while (i > 0 || j > 0) {
    if (i === 0) j--;
    else if (j === 0) i--;
    else {
      const minVal = Math.min(
        dtwMatrix[i - 1][j],
        dtwMatrix[i][j - 1],
        dtwMatrix[i - 1][j - 1]
      );
      if (minVal === dtwMatrix[i - 1][j - 1]) { i--; j--; }
      else if (minVal === dtwMatrix[i - 1][j]) i--;
      else j--;
    }
    path.push([i, j]);
  }
  path.reverse();
  
  // Compute alignment score (0-100)
  // maxPossibleDistance = max range of the two series
  const allValues = [...seriesA, ...seriesB];
  const range = Math.max(...allValues) - Math.min(...allValues);
  const maxPossibleDistance = range || 1;
  const alignmentScore = Math.max(0, (1 - dtwDistance / maxPossibleDistance) * 100);
  
  return { distance: dtwDistance, alignmentScore, path };
}

/**
 * Compute Pearson correlation coefficient between two sequences.
 */
export function pearson(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;
  
  const meanA = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const meanB = b.slice(0, n).reduce((s, v) => s + v, 0) / n;
  
  let numerator = 0, denomA = 0, denomB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA;
    const db = b[i] - meanB;
    numerator += da * db;
    denomA += da * da;
    denomB += db * db;
  }
  
  const denom = Math.sqrt(denomA * denomB);
  return denom === 0 ? 0 : numerator / denom;
}

/**
 * Cross-correlation with lag analysis for AR-4.
 * @returns best correlation coefficient and optimal lag (in samples)
 */
export function crossCorrBest(
  left: number[],
  right: number[],
  maxLagSamples: number
): { bestCorrelation: number; bestLag: number } {
  let bestCorr = 0;
  let bestLag = 0;
  const n = Math.min(left.length, right.length);
  
  for (let lag = -maxLagSamples; lag <= maxLagSamples; lag++) {
    const pairs: [number, number][] = [];
    for (let i = 0; i < n; i++) {
      const j = i + lag;
      if (j >= 0 && j < n) {
        pairs.push([left[i], right[j]]);
      }
    }
    if (pairs.length < 2) continue;
    
    const a = pairs.map(p => p[0]);
    const b = pairs.map(p => p[1]);
    const corr = pearson(a, b);
    
    if (Math.abs(corr) > Math.abs(bestCorr)) {
      bestCorr = corr;
      bestLag = lag;
    }
  }
  
  return { bestCorrelation: bestCorr, bestLag: bestLag };
}
